import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Plus, Search, Edit2, Trash2, IndianRupee, Calendar, Building2, Store, Warehouse, Filter, TrendingUp, FileText, RefreshCw } from 'lucide-react';
import { expensesAPI, storesAPI, warehousesAPI } from '../../services/api';
import { Checkbox } from '../../components/ui/checkbox';
import BulkDeleteBar from '../../components/hq/BulkDeleteBar';

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'debit_card', label: 'Debit Card' },
  { value: 'other', label: 'Other' }
];

const RECURRENCE_TYPES = [
  { value: 'none', label: 'One-time' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' }
];

export default function ExpensesManager() {
  const { toast } = useToast();
  const [expenses, setExpenses] = useState([]);
  const [expenseHeads, setExpenseHeads] = useState([]);
  const [summary, setSummary] = useState({ by_category: [], total_amount: 0, total_count: 0 });
  const [byEntity, setByEntity] = useState({ hq: [], store: [], warehouse: [] });
  const [stores, setStores] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 0 });
  
  // Filters
  const [filters, setFilters] = useState({
    entity_type: '',
    entity_id: '',
    expense_head_id: '',
    date_from: '',
    date_to: ''
  });
  
  const [activeTab, setActiveTab] = useState('list');
  const [showDialog, setShowDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [formData, setFormData] = useState({
    expense_head_id: '',
    amount: 0,
    expense_date: new Date().toISOString().split('T')[0],
    description: '',
    payment_mode: 'cash',
    reference_no: '',
    vendor_name: '',
    is_recurring: false,
    recurrence_type: 'none'
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadExpenses();
  }, [filters, pagination.page]);

  const loadInitialData = async () => {
    try {
      const [headsData, storesData, warehousesData] = await Promise.all([
        expensesAPI.getHeadsDropdown('all'), // Get all expense heads (HQ + All)
        storesAPI.getDropdown(),
        warehousesAPI.getDropdown()
      ]);
      
      // If no expense heads, try seeding defaults
      if (!headsData || headsData.length === 0) {
        try {
          await expensesAPI.seedDefaultHeads();
          const newHeads = await expensesAPI.getHeadsDropdown('all');
          setExpenseHeads(newHeads || []);
        } catch (seedError) {
          console.log('Could not seed default heads:', seedError);
          setExpenseHeads([]);
        }
      } else {
        setExpenseHeads(headsData);
      }
      
      setStores(storesData || []);
      setWarehouses(warehousesData || []);
      
      // Load summary data
      loadSummary();
    } catch (error) {
      console.error('Failed to load initial data:', error);
    }
  };

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        page: pagination.page,
        page_size: 20
      };
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) delete params[key];
      });
      
      // Use unified endpoint to get expenses from HQ, Stores, and Warehouses
      const data = await expensesAPI.listUnified(params);
      setExpenses(data.items || []);
      setPagination({
        page: data.page,
        total: data.total,
        total_pages: data.total_pages
      });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load expenses', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      // Use unified summary endpoint
      const data = await expensesAPI.getUnifiedSummary(filters);
      // Transform for the UI
      setSummary({
        by_category: [],
        total_amount: data.totals?.grand_total || 0,
        total_count: data.totals?.total_count || 0
      });
      // Set by entity data from unified summary
      setByEntity({
        hq: [{ entity_name: 'Headquarters', total_amount: data.totals?.hq?.amount || 0, count: data.totals?.hq?.count || 0 }],
        store: data.by_store || [],
        warehouse: [{ entity_name: 'Warehouses', total_amount: data.totals?.warehouse?.amount || 0, count: data.totals?.warehouse?.count || 0 }]
      });
    } catch (error) {
      console.error('Failed to load summary:', error);
    }
  };

  const loadByEntity = async () => {
    // Now handled by loadSummary using unified endpoint
  };

  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        expense_head_id: expense.expense_head_id || '',
        amount: expense.amount || 0,
        expense_date: expense.expense_date || new Date().toISOString().split('T')[0],
        description: expense.description || '',
        payment_mode: expense.payment_mode || 'cash',
        reference_no: expense.reference_no || '',
        vendor_name: expense.vendor_name || '',
        is_recurring: expense.is_recurring || false,
        recurrence_type: expense.recurrence_type || 'none'
      });
    } else {
      setEditingExpense(null);
      setFormData({
        expense_head_id: '',
        amount: 0,
        expense_date: new Date().toISOString().split('T')[0],
        description: '',
        payment_mode: 'cash',
        reference_no: '',
        vendor_name: '',
        is_recurring: false,
        recurrence_type: 'none'
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.expense_head_id || !formData.amount) {
      toast({ title: 'Error', description: 'Category and Amount are required', variant: 'destructive' });
      return;
    }

    try {
      if (editingExpense) {
        await expensesAPI.update(editingExpense.id, formData);
        toast({ title: 'Success', description: 'Expense updated' });
      } else {
        await expensesAPI.create(formData);
        toast({ title: 'Success', description: 'Expense recorded' });
      }
      setShowDialog(false);
      loadExpenses();
      loadSummary();
      loadByEntity();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Operation failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (expense) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      await expensesAPI.delete(expense.id);
      toast({ title: 'Success', description: 'Expense deleted' });
      loadExpenses();
      loadSummary();
      loadByEntity();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete expense', variant: 'destructive' });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getEntityIcon = (type) => {
    switch (type) {
      case 'hq': return <Building2 className="w-4 h-4" />;
      case 'store': return <Store className="w-4 h-4" />;
      case 'warehouse': return <Warehouse className="w-4 h-4" />;
      default: return <Building2 className="w-4 h-4" />;
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="expenses-manager-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Expenses</h1>
          <p className="text-gray-500">Monitor and manage expenses across all entities</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-expense-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Expense
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-blue-100 text-sm">Total Expenses</p>
                <p className="text-2xl font-bold">{formatCurrency(summary.total_amount)}</p>
                <p className="text-sm text-blue-100">{summary.total_count} entries</p>
              </div>
              <IndianRupee className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-purple-100 text-sm">HQ Expenses</p>
                <p className="text-2xl font-bold">{formatCurrency(byEntity.hq.reduce((sum, e) => sum + e.total_amount, 0))}</p>
                <p className="text-sm text-purple-100">{byEntity.hq.length} records</p>
              </div>
              <Building2 className="w-8 h-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-green-100 text-sm">Store Expenses</p>
                <p className="text-2xl font-bold">{formatCurrency(byEntity.store.reduce((sum, e) => sum + e.total_amount, 0))}</p>
                <p className="text-sm text-green-100">{byEntity.store.length} stores</p>
              </div>
              <Store className="w-8 h-8 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="p-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-orange-100 text-sm">Warehouse Expenses</p>
                <p className="text-2xl font-bold">{formatCurrency(byEntity.warehouse.reduce((sum, e) => sum + e.total_amount, 0))}</p>
                <p className="text-sm text-orange-100">{byEntity.warehouse.length} warehouses</p>
              </div>
              <Warehouse className="w-8 h-8 text-orange-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="list" className="gap-2">
            <FileText className="w-4 h-4" /> All Expenses
          </TabsTrigger>
          <TabsTrigger value="by-category" className="gap-2">
            <TrendingUp className="w-4 h-4" /> By Category
          </TabsTrigger>
          <TabsTrigger value="by-entity" className="gap-2">
            <Building2 className="w-4 h-4" /> By Entity
          </TabsTrigger>
        </TabsList>

        {/* Filters */}
        <div className="flex gap-4 mt-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <Label className="text-xs">Entity Type</Label>
            <Select value={filters.entity_type || 'all'} onValueChange={(v) => setFilters({ ...filters, entity_type: v === 'all' ? '' : v, entity_id: '' })}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="All Entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                <SelectItem value="hq">HQ</SelectItem>
                <SelectItem value="store">Stores</SelectItem>
                <SelectItem value="warehouse">Warehouses</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filters.entity_type === 'store' && (
            <div className="flex-1">
              <Label className="text-xs">Store</Label>
              <Select value={filters.entity_id || 'all'} onValueChange={(v) => setFilters({ ...filters, entity_id: v === 'all' ? '' : v })}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All Stores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stores</SelectItem>
                  {stores.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {filters.entity_type === 'warehouse' && (
            <div className="flex-1">
              <Label className="text-xs">Warehouse</Label>
              <Select value={filters.entity_id || 'all'} onValueChange={(v) => setFilters({ ...filters, entity_id: v === 'all' ? '' : v })}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All Warehouses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Warehouses</SelectItem>
                  {warehouses.map(w => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex-1">
            <Label className="text-xs">Category</Label>
            <Select value={filters.expense_head_id || 'all'} onValueChange={(v) => setFilters({ ...filters, expense_head_id: v === 'all' ? '' : v })}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {expenseHeads.map(h => (
                  <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1">
            <Label className="text-xs">From Date</Label>
            <Input
              type="date"
              className="h-9"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            />
          </div>

          <div className="flex-1">
            <Label className="text-xs">To Date</Label>
            <Input
              type="date"
              className="h-9"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            />
          </div>

          <div className="flex items-end">
            <Button variant="outline" size="sm" onClick={() => setFilters({ entity_type: '', entity_id: '', expense_head_id: '', date_from: '', date_to: '' })}>
              Clear
            </Button>
          </div>
        </div>

        {/* Expenses List Tab */}
        <TabsContent value="list" className="mt-4">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : expenses.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <IndianRupee className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No expenses found</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                {selectedIds.length > 0 && (
                  <div className="p-3 border-b border-gray-200">
                    <BulkDeleteBar
                      selectedIds={selectedIds}
                      entityLabel="expense"
                      endpoint="/hq/bulk-delete/expenses"
                      onCleared={() => { setSelectedIds([]); loadExpenses(); loadSummary(); }}
                    />
                  </div>
                )}
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-3 w-10">
                        <Checkbox
                          checked={expenses.length > 0 && selectedIds.length === expenses.length}
                          onCheckedChange={(c) => setSelectedIds(c ? expenses.map(e => e.id) : [])}
                          data-testid="expenses-select-all"
                        />
                      </th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Date</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Category</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Entity</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Description</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Payment</th>
                      <th className="text-right p-3 text-sm font-medium text-gray-600">Amount</th>
                      <th className="text-center p-3 text-sm font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map((expense) => (
                      <tr key={expense.id} className="border-t hover:bg-gray-50" data-testid={`expense-row-${expense.id}`}>
                        <td className="p-3">
                          <Checkbox
                            checked={selectedIds.includes(expense.id)}
                            onCheckedChange={(c) => setSelectedIds(prev => c ? [...prev, expense.id] : prev.filter(x => x !== expense.id))}
                            data-testid={`expense-select-${expense.id}`}
                          />
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            {formatDate(expense.expense_date)}
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline">{expense.expense_head_code}</Badge>
                          <span className="ml-2 text-sm">{expense.expense_head_name}</span>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {getEntityIcon(expense.entity_type)}
                            <span className="text-sm">{expense.entity_name || expense.entity_type?.toUpperCase()}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="text-sm text-gray-600">{expense.description || '-'}</span>
                          {expense.vendor_name && (
                            <span className="text-xs text-gray-400 block">Paid to: {expense.vendor_name}</span>
                          )}
                        </td>
                        <td className="p-3">
                          <Badge variant="secondary" className="text-xs">
                            {expense.payment_mode?.replace('_', ' ')}
                          </Badge>
                          {expense.is_recurring && (
                            <Badge variant="outline" className="ml-1 text-xs gap-1">
                              <RefreshCw className="w-3 h-3" />
                            </Badge>
                          )}
                        </td>
                        <td className="p-3 text-right font-medium text-red-600">
                          {formatCurrency(expense.amount)}
                        </td>
                        <td className="p-3 text-center">
                          {expense._source === 'hq' || expense.entity_type === 'hq' ? (
                            <div className="flex justify-center gap-1">
                              <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(expense)}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(expense)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">View Only</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {pagination.total_pages > 1 && (
                  <div className="flex justify-between items-center p-4 border-t">
                    <span className="text-sm text-gray-500">
                      Showing {((pagination.page - 1) * 20) + 1} - {Math.min(pagination.page * 20, pagination.total)} of {pagination.total}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.page === 1}
                        onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.page === pagination.total_pages}
                        onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* By Category Tab */}
        <TabsContent value="by-category" className="mt-4">
          <div className="grid gap-3">
            {summary.by_category.map((cat) => (
              <Card key={cat._id}>
                <CardContent className="py-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">{cat.expense_head_code}</Badge>
                      <span className="font-medium">{cat.expense_head_name}</span>
                      <span className="text-sm text-gray-500">({cat.count} entries)</span>
                    </div>
                    <span className="text-lg font-bold text-red-600">{formatCurrency(cat.total_amount)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* By Entity Tab */}
        <TabsContent value="by-entity" className="mt-4">
          <div className="grid grid-cols-3 gap-4">
            {/* HQ */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building2 className="w-5 h-5 text-purple-600" /> HQ
                </CardTitle>
              </CardHeader>
              <CardContent>
                {byEntity.hq.length === 0 ? (
                  <p className="text-sm text-gray-500">No expenses</p>
                ) : (
                  byEntity.hq.map((e, i) => (
                    <div key={i} className="flex justify-between py-2 border-b last:border-0">
                      <span className="text-sm">{e.entity_name}</span>
                      <span className="font-medium text-red-600">{formatCurrency(e.total_amount)}</span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Stores */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Store className="w-5 h-5 text-green-600" /> Stores
                </CardTitle>
              </CardHeader>
              <CardContent>
                {byEntity.store.length === 0 ? (
                  <p className="text-sm text-gray-500">No expenses</p>
                ) : (
                  byEntity.store.map((e, i) => (
                    <div key={i} className="flex justify-between py-2 border-b last:border-0">
                      <span className="text-sm">{e.entity_name}</span>
                      <span className="font-medium text-red-600">{formatCurrency(e.total_amount)}</span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Warehouses */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Warehouse className="w-5 h-5 text-orange-600" /> Warehouses
                </CardTitle>
              </CardHeader>
              <CardContent>
                {byEntity.warehouse.length === 0 ? (
                  <p className="text-sm text-gray-500">No expenses</p>
                ) : (
                  byEntity.warehouse.map((e, i) => (
                    <div key={i} className="flex justify-between py-2 border-b last:border-0">
                      <span className="text-sm">{e.entity_name}</span>
                      <span className="font-medium text-red-600">{formatCurrency(e.total_amount)}</span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={formData.expense_head_id} onValueChange={(v) => setFormData({ ...formData, expense_head_id: v })}>
                  <SelectTrigger data-testid="expense-head-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseHeads.map(h => (
                      <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  data-testid="expense-amount-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Mode</Label>
                <Select value={formData.payment_mode} onValueChange={(v) => setFormData({ ...formData, payment_mode: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_MODES.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Expense description..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Paid To (Vendor)</Label>
                <Input
                  value={formData.vendor_name}
                  onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                  placeholder="Vendor name"
                />
              </div>
              <div className="space-y-2">
                <Label>Reference No.</Label>
                <Input
                  value={formData.reference_no}
                  onChange={(e) => setFormData({ ...formData, reference_no: e.target.value })}
                  placeholder="Cheque/Transaction ID"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={formData.is_recurring}
                onCheckedChange={(v) => setFormData({ ...formData, is_recurring: v })}
              />
              <Label>Recurring Expense</Label>
            </div>

            {formData.is_recurring && (
              <div className="space-y-2">
                <Label>Recurrence</Label>
                <Select value={formData.recurrence_type} onValueChange={(v) => setFormData({ ...formData, recurrence_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECURRENCE_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-expense-btn">
              {editingExpense ? 'Update' : 'Add Expense'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
